// Searchable multi-select picker to add leads to a route. Leads already in the
// route are shown as "Added" and disabled.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FaSearch, FaCheckSquare, FaRegSquare, FaSpinner, FaMapMarkerAlt } from 'react-icons/fa';
import Modal from '../../../components/shared/Modal';
import formatPhone from '../../../components/shared/formatPhone';
import { getLeads } from '../../leads/leadService';

const Search = styled.div`
  display: flex; align-items: center; gap: 9px; margin-bottom: 12px; padding: 9px 12px;
  background: ${p => p.theme.colors.background}; border: 1.5px solid ${p => p.theme.colors.inputGray}44; border-radius: 10px;
  svg { color: ${p => p.theme.colors.textSecondary}; }
  input { flex: 1; background: transparent; border: none; outline: none; color: ${p => p.theme.colors.textPrimary}; font-size: 0.92rem; }
`;
const ListBox = styled.div`
  max-height: 46vh; overflow-y: auto; border: 1px solid ${p => p.theme.colors.inputGray}22; border-radius: 10px;
`;
const Row = styled.button`
  display: flex; align-items: center; gap: 11px; width: 100%; text-align: left; padding: 10px 12px;
  background: transparent; border: none; border-bottom: 1px solid ${p => p.theme.colors.inputGray}1a; cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover:not(:disabled) { background: ${p => p.theme.colors.primary}12; }
  &:disabled { opacity: 0.5; cursor: default; }
  .chk { color: ${p => p.$checked ? p.theme.colors.primary : p.theme.colors.inputGray}; font-size: 18px; flex-shrink: 0; }
  .info { flex: 1; min-width: 0; }
  .name { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 0.9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { color: ${p => p.theme.colors.textSecondary}; font-size: 0.78rem; margin-top: 2px; display: flex; align-items: center; gap: 6px; }
  .added { color: ${p => p.theme.colors.success}; font-size: 0.72rem; font-weight: 700; }
`;
const Foot = styled.div`margin-top: 12px; color: ${p => p.theme.colors.textSecondary}; font-size: 0.84rem;`;

const AddLeadsModal = ({ isOpen, existingIds = [], onClose, onAdd }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(new Set());

  useEffect(() => {
    if (!isOpen) return;
    setPicked(new Set()); setQuery('');
    setLoading(true);
    getLeads().then(setLeads).catch(() => setLeads([])).finally(() => setLoading(false));
  }, [isOpen]);

  const have = useMemo(() => new Set(existingIds), [existingIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads.slice(0, 300);
    return leads.filter(l =>
      l.name?.toLowerCase().includes(q) ||
      l.city?.toLowerCase().includes(q) ||
      l.phone?.includes(q) ||
      l.email?.toLowerCase().includes(q)
    ).slice(0, 300);
  }, [leads, query]);

  const toggle = useCallback((id) => {
    setPicked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  if (!isOpen) return null;

  return (
    <Modal title="Add Leads to Route" isOpen={isOpen} onCancel={onClose} onConfirm={() => onAdd([...picked])}>
      <Search>
        <FaSearch />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, city, phone…" autoFocus />
      </Search>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#F69620' }}><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : (
        <ListBox>
          {filtered.map(lead => {
            const added = have.has(lead.id);
            const checked = picked.has(lead.id);
            return (
              <Row key={lead.id} $checked={checked} disabled={added} onClick={() => !added && toggle(lead.id)}>
                <span className="chk">{added ? <FaCheckSquare /> : checked ? <FaCheckSquare /> : <FaRegSquare />}</span>
                <span className="info">
                  <div className="name">{lead.name || 'Lead #' + lead.id}</div>
                  <div className="sub">
                    {[lead.city, lead.phone && formatPhone(lead.phone)].filter(Boolean).join(' · ')}
                    {!lead.latitude && <span style={{ color: '#e0a' }}><FaMapMarkerAlt size={9} /> no map loc</span>}
                  </div>
                </span>
                {added && <span className="added">Added</span>}
              </Row>
            );
          })}
          {filtered.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>No leads found</div>}
        </ListBox>
      )}

      <Foot>{picked.size} selected</Foot>
    </Modal>
  );
};

export default AddLeadsModal;
